import React from 'react';
import { useTranslation } from 'react-i18next';

const TaskPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

const TaskCard = ({ task, users, onEdit, onDelete, onDragStart }) => {
    const { t } = useTranslation();
    const getPriorityColor = (priority) => {
        switch (priority) {
            case TaskPriority.LOW:
                return 'bg-gray-100 text-gray-700 border-gray-300';
            case TaskPriority.MEDIUM:
                return 'bg-blue-100 text-blue-700 border-blue-300';
            case TaskPriority.HIGH:
                return 'bg-orange-100 text-orange-700 border-orange-300';
            case TaskPriority.CRITICAL:
                return 'bg-red-100 text-red-700 border-red-300';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-300';
        }
    };

    const getPriorityLabel = (priority) => {
        switch (priority) {
            case TaskPriority.LOW:
                return t('task.priority.low');
            case TaskPriority.MEDIUM:
                return t('task.priority.medium');
            case TaskPriority.HIGH:
                return t('task.priority.high');
            case TaskPriority.CRITICAL:
                return t('task.priority.critical');
            default:
                return priority;
        }
    };

    const getUserFullName = (userId) => {
        if (!userId || !users) return t('common.unassigned');
        const user = users.find(u => u.id === userId);
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || t('common.unassigned');
        }
        return t('common.unassigned');
    };

    return (
        <div
            draggable
            onDragStart={(e) => onDragStart(e, task)}
            className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow cursor-move"
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-800 text-sm flex-1 pr-2">
                    {task.title}
                </h3>
                <div className="flex gap-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(task);
                        }}
                        className="text-gray-400 hover:text-blue-600"
                        title={t('task.editTask')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(task.id);
                        }}
                        className="text-gray-400 hover:text-red-600"
                        title={t('task.deleteTask')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Description */}
            {task.description && (
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                    {task.description}
                </p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between">
                {/* Priority Badge */}
                <span className={`text-xs font-medium px-2 py-1 rounded border ${getPriorityColor(task.priority)}`}>
                    {getPriorityLabel(task.priority)}
                </span>

                {/* Assignee */}
                <div className="flex items-center gap-1" title={getUserFullName(task.assignee)}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-600 truncate max-w-[80px]">
                        {getUserFullName(task.assignee)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TaskCard;
